/**
 * Per-request UMK-proof MAC. Spec §8.
 *
 *   mac = HMAC-SHA-256( HKDF(UMK, info='umk-proof-v1', salt=userId),
 *                       method ‖ ':' ‖ path ‖ ':' ‖ nonceHeader ‖ ':' ‖ bodyHash )
 *
 * Default construction: `hkdf-hmac-v1`. The `eddsa-v1` variant is reserved for
 * the external reviewer's call (R5 / trade-off 3) — keeping `algorithm` in the
 * signature makes a future swap a second branch, not a rewrite.
 */
import { deriveUmkProofKey } from './hkdf.js';
import { UmkProofKeyDerivationError } from './errors.js';
import { toArrayBuffer } from './bytes.js';
const enc = new TextEncoder();
function buildMessage(method, path, nonceHeader, bodyHash) {
    const prefix = enc.encode(`${method}:${path}:${nonceHeader}:`);
    const out = new Uint8Array(prefix.length + bodyHash.length);
    out.set(prefix, 0);
    out.set(bodyHash, prefix.length);
    return out;
}
/** Compute the UMK-proof MAC for a request. */
export async function computeUmkProof(args) {
    const algorithm = args.algorithm ?? 'hkdf-hmac-v1';
    // Only one algorithm is implemented in v1; the param guards future expansion.
    if (algorithm !== 'hkdf-hmac-v1') {
        throw new UmkProofKeyDerivationError();
    }
    let macKeyBytes;
    try {
        macKeyBytes = await deriveUmkProofKey(args.umk, args.userId);
    }
    catch {
        throw new UmkProofKeyDerivationError();
    }
    const macKey = await crypto.subtle.importKey('raw', toArrayBuffer(macKeyBytes), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const message = buildMessage(args.method, args.path, args.nonceHeader, args.bodyHash);
    const sig = await crypto.subtle.sign('HMAC', macKey, toArrayBuffer(message));
    return new Uint8Array(sig);
}
//# sourceMappingURL=umkProof.js.map